import { Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import {
    FetchAllUsers,
    FetchAllUsersFailure,
    FetchAllUsersSuccess,
    FetchCertificateOwners,
    FetchGeneralGroupMembers,
    FetchGeneralGroupMembersFailure,
    FetchGeneralGroupMembersSuccess,
    FetchParticipantsAndResponsibles,
    FetchParticipantsAndResponsiblesFailure,
    FetchParticipantsAndResponsiblesSuccess,
    FetchTeamMembersUsers,
} from '../actions';
import { ExtendedAttributeFilterInterface } from '../interfaces/extended-attribute-filer.interface';
import { AllUsersState } from '../models/all-users.state';

@Injectable()
export class AllUsersService {
    @Select(AllUsersState.getEntities)
    allUsers$: Observable<User[]>;

    constructor(private _store: Store, private _actionHelper: NgxsActionHelper) {}

    getAllUsers(): User[] {
        return this._store.selectSnapshot(AllUsersState.getEntities);
    }

    async fetchParticipantsAndResponsibles(
        moduleId: string,
        groupId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number
    ): Promise<boolean> {
        this._store.dispatch(new FetchParticipantsAndResponsibles({ moduleId, groupId, search, sortBy, sortOrder, pageSize, pageNumber }));

        return await this._actionHelper.race(FetchParticipantsAndResponsiblesSuccess, FetchParticipantsAndResponsiblesFailure).toPromise();
    }

    async fetchGeneralGroupMembers(
        moduleId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number
    ): Promise<boolean> {
        this._store.dispatch(new FetchGeneralGroupMembers({ moduleId, search, sortBy, sortOrder, pageSize, pageNumber }));

        return await this._actionHelper.race(FetchGeneralGroupMembersSuccess, FetchGeneralGroupMembersFailure).toPromise();
    }

    async fetchAllUsers(
        moduleIds: string[],
        entityId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number,
        isUnassignedSelected?: boolean,
        role?: string,
        includeAnalytics?: boolean,
        experienceIds?: string[],
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedExperiences?: boolean,
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        columns?: string[],
        userRole?: string[],
        userStatuses?: string[],
        statusIds?: string[]
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchAllUsers({
                moduleIds,
                entityId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber: pageNumber,
                isUnassignedSelected,
                role,
                includeAnalytics,
                experienceIds,
                audienceIds,
                teamIds,
                extendedAttributesFilters,
                isUnassignedSelectedExperiences,
                isUnassignedSelectedAudiences,
                isUnassignedSelectedTeams,
                columns,
                userRole,
                userStatuses,
                statusIds,
            })
        );

        return await this._actionHelper.race(FetchAllUsersSuccess, FetchAllUsersFailure).toPromise();
    }

    async fetchCertificateOwners(search?: string, sortBy?: string, sortOrder?: string, pageSize?: number, pageNumber?: number): Promise<boolean> {
        this._store.dispatch(
            new FetchCertificateOwners({
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber: pageNumber,
            })
        );

        return await this._actionHelper.race(FetchAllUsersSuccess, FetchAllUsersFailure).toPromise();
    }

    async fetchTeamMembersUsers(
        teamId: string,
        search?: string,
        sortBy?: string,
        sortOrder?: string,
        pageSize?: number,
        pageNumber?: number,
        includeSubteams?: boolean
    ): Promise<boolean> {
        this._store.dispatch(
            new FetchTeamMembersUsers({
                teamId,
                search,
                sortBy,
                sortOrder,
                pageSize,
                pageNumber: pageNumber,
                includeSubteams,
            })
        );

        return await this._actionHelper.race(FetchAllUsersSuccess, FetchAllUsersFailure).toPromise();
    }
}
